import {
  getDocumentById,
  getCollection,
  addDocToCollection,
  updateDocById,
} from '../_shared/firestore/index.js';
import { SafetyReportModel } from '@cmms/models';
import { createCMMSView } from '../views/views.js';
import { getAuth } from 'firebase/auth';

const COLLECTION_NAME = 'safety_reports';

// Helper function to get current user's company ID
async function getCurrentUserCompanyId(db) {
  console.log('🔧 getCurrentUserCompanyId (Safety Reports): Starting...');
  
  const auth = getAuth(db.app);
  const currentUser = auth.currentUser;
  
  console.log('🔧 getCurrentUserCompanyId (Safety Reports): currentUser:', currentUser ? 'authenticated' : 'not authenticated');
  
  if (!currentUser) {
    console.error('🔧 getCurrentUserCompanyId (Safety Reports): No authenticated user');
    throw new Error('User not authenticated');
  }

  console.log('🔧 getCurrentUserCompanyId (Safety Reports): Getting user doc for uid:', currentUser.uid);
  const userDoc = await getDocumentById(db, 'users', currentUser.uid);
  
  console.log('🔧 getCurrentUserCompanyId (Safety Reports): userDoc:', userDoc);
  
  if (!userDoc || !userDoc.company_id) {
    console.error('🔧 getCurrentUserCompanyId (Safety Reports): User company not found in userDoc:', userDoc);
    throw new Error('User company not found');
  }

  console.log('🔧 getCurrentUserCompanyId (Safety Reports): Found companyId:', userDoc.company_id);
  return userDoc.company_id;
}

async function getSafetyReportsByCompany(db, companyId) {
  try {
    const docs = await getCollection(db, COLLECTION_NAME, {
      key: 'company_id',
      operator: '==',
      value: companyId,
    });
    const items = docs.map((doc) => ({ id: doc.$id, model: new SafetyReportModel(doc) }));
    return createCMMSView({ data: items });
  } catch (error) {
    return createCMMSView({ error });
  }
}

async function getSafetyReportsByStatus(db, companyId, status) {
  try {
    const docs = await getCollection(db, COLLECTION_NAME, [
      { key: 'company_id', operator: '==', value: companyId },
      { key: 'status', operator: '==', value: status },
    ]);
    const items = docs.map((doc) => ({ id: doc.$id, model: new SafetyReportModel(doc) }));
    return createCMMSView({ data: items });
  } catch (error) {
    return createCMMSView({ error });
  }
}

async function getSafetyReportById(db, id) {
  try {
    const companyId = await getCurrentUserCompanyId(db);
    const doc = await getDocumentById(db, COLLECTION_NAME, id);
    if (!doc) return createCMMSView({ error: 'Safety report not found' });
    if (doc.company_id !== companyId) {
      return createCMMSView({ error: 'Access denied: Safety report not found' });
    }
    return createCMMSView({ data: { id: doc.$id, model: new SafetyReportModel(doc) } });
  } catch (error) {
    return createCMMSView({ error });
  }
}

async function createSafetyReport(db, payload) {
  try {
    console.log('🔧 createSafetyReport: Starting with payload:', payload);
    
    const companyId = await getCurrentUserCompanyId(db);
    const auth = getAuth(db.app);
    const currentUser = auth.currentUser;
    const userId = currentUser ? currentUser.uid : '';
    
    console.log('🔧 createSafetyReport: Got companyId:', companyId);
    console.log('🔧 createSafetyReport: Current user ID:', userId);
    
    const payloadWithCompany = {
      ...payload,
      company_id: companyId,
      reported_by_user_id: userId // Add the current user ID as reporter
    };
    console.log('🔧 createSafetyReport: payloadWithCompany:', payloadWithCompany);
    
    const model = new SafetyReportModel(payloadWithCompany);
    console.log('🔧 createSafetyReport: Created model, calling toFirestore()');
    
    const firestoreData = model.toFirestore();
    console.log('🔧 createSafetyReport: Firestore data to save:', firestoreData);
    console.log('🔧 createSafetyReport: Attempting to save to collection:', COLLECTION_NAME);

    const ref = await addDocToCollection(db, COLLECTION_NAME, firestoreData);
    console.log('🔧 createSafetyReport: Successfully created report with ref:', ref);

    return createCMMSView({ data: ref });
  } catch (error) {
    console.error('🔧 createSafetyReport: Error occurred:', error);
    console.error('🔧 createSafetyReport: Error message:', error.message);
    console.error('🔧 createSafetyReport: Error stack:', error.stack);
    return createCMMSView({ error });
  }
}

async function updateSafetyReport(db, id, updateData) {
  try {
    const companyId = await getCurrentUserCompanyId(db);
    const existing = await getDocumentById(db, COLLECTION_NAME, id);
    if (!existing) return createCMMSView({ error: 'Safety report not found' });
    if (existing.company_id !== companyId) {
      return createCMMSView({ error: 'Access denied: Safety report not found' });
    }
    const merged = { ...existing, ...updateData, updated_at: Date.now() };
    const normalized = new SafetyReportModel(merged).toFirestore();
    const finalUpdate = { ...merged, ...normalized };
    await updateDocById(db, COLLECTION_NAME, id, finalUpdate);
    return createCMMSView({ data: { id, ...finalUpdate } });
  } catch (error) {
    return createCMMSView({ error });
  }
}

export {
  getSafetyReportsByCompany,
  getSafetyReportsByStatus,
  getSafetyReportById,
  createSafetyReport,
  updateSafetyReport,
};


